<template>
  <div class="message-composer">
    <div class="message-composer__avatar-wrap" aria-hidden="true">
      <img v-if="resolvedAvatar" :src="resolvedAvatar" class="message-composer__avatar" alt="avatar" />
      <div v-else class="message-composer__avatar message-composer__avatar--fallback">
        {{ resolvedInitial }}
      </div>
    </div>
    <div class="message-composer__editor">
      <b-form-textarea
        v-model="localContent"
        :rows="5"
        :max-rows="50"
        no-resize
        :placeholder="placeholder"
        :maxlength="effectiveMaxLength > 0 ? effectiveMaxLength : null"
        class="message-composer__textarea"
        @keydown.enter="onEnterKey"
        @paste="onPaste"
      />
      <div class="message-composer__footer">
        <span
          v-if="effectiveMaxLength > 0"
          class="message-composer__counter"
          :class="{ 'message-composer__counter--warn': isNearLimit }"
        >
          {{ contentLength }} / {{ effectiveMaxLength }}
        </span>
        <div class="message-composer__actions">
          <b-button
          v-if="showCancel"
          variant="light"
          class="message-composer__btn message-composer__btn--cancel"
          @click="$emit('cancel')"
        >
          {{ cancelText }}
        </b-button>
        <b-button
          variant="primary"
          class="message-composer__btn"
          :class="{ 'message-composer__btn--busy': submitting }"
          :disabled="disabled || !canSubmit || submitting"
          :aria-busy="submitting ? 'true' : 'false'"
          v-bind="submitTLabel ? { t_label: submitTLabel } : {}"
          @click="submit"
        >
          <span class="message-composer__btn-inner">
            <span v-if="submitting" class="message-composer__btn-spinner" aria-hidden="true"></span>
            <span class="message-composer__btn-text">{{ submitText }}</span>
          </span>
        </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { resolveDisplayInitial } from "@/_services/messageBoard.service";

export default {
  name: "MessageComposer",
  props: {
    value: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "Type your message..."
    },
    submitText: {
      type: String,
      default: "Post"
    },
    cancelText: {
      type: String,
      default: "Cancel"
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    },
    avatar: {
      type: String,
      default: ""
    },
    avatarName: {
      type: String,
      default: ""
    },
    submitTLabel: {
      type: String,
      default: ""
    },
    /** 0 表示不限制字数 */
    maxLength: {
      type: Number,
      default: 1500
    },
    /** 为 true 时同步未保存草稿到 $store.state.dirty（销售线索离开确认） */
    enableLeaveGuard: {
      type: Boolean,
      default: false
    },
    /** 为 true 时允许换行；默认 false，输入时不保留回车符 */
    allowLineBreak: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localContent: ""
    };
  },
  created() {
    this.localContent = this.normalizeInputContent(this.value);
  },
  computed: {
    currentUser() {
      return this.$store?.state?.authentication?.user || {};
    },
    currentUserAvatar() {
      const u = this.currentUser || {};
      const logoListItem = Array.isArray(u?.userLogoList) && u.userLogoList.length > 0 ? u.userLogoList[0] : null;
      return (
        u?.userLogo ||
        u?.UserLogo ||
        u?.userlogo ||
        u?.avatar ||
        u?.Avatar ||
        u?.photo ||
        u?.Photo ||
        u?.logo ||
        u?.Logo ||
        u?.logoFileName ||
        u?.LogoFileName ||
        logoListItem?.imageSrc ||
        logoListItem?.cdnLnk ||
        logoListItem?.fileName ||
        ""
      );
    },
    currentUserDisplayName() {
      const u = this.currentUser || {};
      return (
        u?.personName ||
        u?.personNameEn ||
        u?.personNameSc ||
        u?.displayName ||
        u?.userName ||
        u?.loginUser ||
        ""
      );
    },
    currentUserInitial() {
      const name = resolveDisplayInitial(this.currentUserDisplayName, "");
      if (name) return name;
      const role = resolveDisplayInitial(this.currentUser?.userType, "");
      if (role) return role;
      return "U";
    },
    resolvedAvatar() {
      const raw = this.avatar || this.currentUserAvatar || "";
      return this.normalizeAvatarUrl(raw);
    },
    resolvedInitial() {
      const name = resolveDisplayInitial(
        this.avatarName || this.currentUserDisplayName,
        ""
      );
      if (name) return name;
      return this.currentUserInitial;
    },
    effectiveMaxLength() {
      const n = Number(this.maxLength);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    },
    contentLength() {
      return String(this.localContent || "").length;
    },
    isNearLimit() {
      if (this.effectiveMaxLength <= 0) return false;
      return this.contentLength >= this.effectiveMaxLength * 0.9;
    },
    canSubmit() {
      const text = String(this.localContent || "").trim();
      if (!text) return false;
      if (this.effectiveMaxLength > 0 && this.contentLength > this.effectiveMaxLength) {
        return false;
      }
      return true;
    }
  },
  watch: {
    value(next) {
      this.localContent = this.normalizeInputContent(next);
    },
    localContent(next) {
      const normalized = this.normalizeInputContent(next);
      if (normalized !== next) {
        this.localContent = normalized;
        return;
      }
      if (this.effectiveMaxLength > 0 && normalized && normalized.length > this.effectiveMaxLength) {
        this.localContent = normalized.slice(0, this.effectiveMaxLength);
        return;
      }
      this.$emit("input", normalized);
      this.syncLeaveGuardDirty();
    }
  },
  mounted() {
    if (this.enableLeaveGuard) {
      this.syncLeaveGuardDirty(false);
    }
  },
  beforeDestroy() {
    if (this.enableLeaveGuard) {
      this.syncLeaveGuardDirty(false);
    }
  },
  methods: {
    normalizeInputContent(text) {
      const raw = String(text ?? "");
      if (this.allowLineBreak) {
        return raw;
      }
      return raw.replace(/[\r\n]+/g, "");
    },
    onEnterKey(event) {
      if (!this.allowLineBreak) {
        event.preventDefault();
      }
    },
    onPaste(event) {
      if (this.allowLineBreak) return;
      const pasted = event.clipboardData?.getData("text") || "";
      if (!/[\r\n]/.test(pasted)) return;
      event.preventDefault();
      const cleaned = this.normalizeInputContent(pasted);
      const el = event.target;
      if (!el || typeof el.selectionStart !== "number") {
        this.localContent = `${this.localContent || ""}${cleaned}`;
        return;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const current = String(this.localContent || "");
      this.localContent = `${current.slice(0, start)}${cleaned}${current.slice(end)}`;
    },
    syncLeaveGuardDirty(forceValue) {
      if (!this.enableLeaveGuard) return;
      const dirty =
        forceValue === undefined
          ? !!String(this.localContent || "").trim()
          : !!forceValue;
      this.$emit("dirty-change", dirty);
    },
    normalizeAvatarUrl(raw) {
      const value = String(raw || "").trim();
      if (!value) return "";
      if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) {
        return value;
      }
      const ossOrigin = String(process.env.VUE_APP_OSS_ORIGIN || "").trim();
      if (ossOrigin) {
        const base = ossOrigin.endsWith("/") ? ossOrigin : `${ossOrigin}/`;
        return `${base}${value.replace(/^\/+/, "")}`;
      }
      return `https://cdn.adsalecdn.com/${value.replace(/^\/+/, "")}`;
    },
    submit() {
      if (!this.canSubmit || this.submitting) return;
      const content = this.normalizeInputContent(this.localContent).trim();
      if (!content) return;
      this.$emit("submit", content);
      this.localContent = "";
      this.$emit("input", "");
      this.syncLeaveGuardDirty(false);
    }
  }
};
</script>

<style scoped>
.message-composer {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.message-composer__avatar-wrap {
  flex: 0 0 28px;
}

.message-composer__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.message-composer__avatar--fallback {
  background: #f2f4f7;
  color: #667085;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.message-composer__editor {
  flex: 1;
  min-width: 0;
  position: relative;
  border: 1px solid #ececf2;
  border-radius: 10px;
  padding: 6px 10px 48px;
}

.message-composer__textarea {
  width: 100%;
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding-left: 0;
  padding-right: 0;
  font-size: 14px;
  min-height: 96px;
  line-height: 1.45;
  resize: none;
  overflow-y: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.message-composer__textarea:focus {
  outline: none;
  box-shadow: none;
}

.message-composer__textarea::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.message-composer__footer {
  position: absolute;
  left: 10px;
  right: 6px;
  bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  background: #fff;
}

.message-composer__counter {
  margin-right: auto;
  font-size: 11px;
  line-height: 1;
  color: #98a2b3;
  white-space: nowrap;
}

.message-composer__counter--warn {
  color: #f04438;
}

.message-composer__actions {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.message-composer__btn {
  min-width:  72px;
  padding: 8px;
  border-radius: 6px;
  font-size: 14px;
}

.message-composer__btn.btn-primary {
  background-color: #d6d8da;
  border-color: #d6d8da;
  color: #fff;
}

.message-composer__btn.btn-primary:disabled {
  background-color: #e4e7ec;
  border-color: #e4e7ec;
  color: #fff;
}

.message-composer__btn--busy.btn-primary:disabled {
  background-color: #7366ff;
  border-color: #7366ff;
  color: #fff;
  opacity: 1;
}

.message-composer__btn-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.message-composer__btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: message-composer-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes message-composer-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
